// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Category } from "@insite/client-framework/Services/CategoryService";
import { getCategoriesDataView } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import { getPageLinkByNodeId, getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Icon from "@insite/mobius/Icon";
import Link from "@insite/mobius/Link";
import { LinkPresentationProps } from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import TitleTypography, { TitleTypographyProps } from "@plumberssupply/App/Components/TitleTypography/TitleTypography";
import { CustomAppState } from "@plumberssupply/App/Store/Reducers";
import { getSettingsCollection } from "@plumberssupply/App/Store/Selectors/SettingsSelectors";
import LazyImage from "@plumberssupply/MobiusOverrides/LazyImage";
import LogoShort from "@plumberssupply/XDL/Icons/LogoShort";
import { color, layoutContainer, xScreen } from "@plumberssupply/XDL/Utilities";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    categories = "categories",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.categories]: {
            fields: {
                id: string;
                link: LinkFieldValue;
                title: boolean;
            };
        }[];
    };
}

const mapStateToProps = (state: CustomAppState) => ({
    categoriesDataView: getCategoriesDataView(state),
    notFoundImagePath: getSettingsCollection(state).customSettings.notFoundMediumImagePath,
    signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
    isTablet: state.xCommon.isTabletScreen,
    isMobile: state.xCommon.isMobileScreen,
    categoryListPageUrl: getPageLinkByPageType(state, "CategoryListPage")?.url,
});

const mapDispatchToProps = {
    loadCategories,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ShopByCategoryStyles {
    wrapper?: InjectableCss;
    container?: InjectableCss;
    titleText?: TitleTypographyProps;
    TitleText?: TypographyPresentationProps;
    categoryImage?: InjectableCss;
    categoriesWrapper?: InjectableCss;
    categoryItem?: InjectableCss;
    categoryLink?: LinkPresentationProps;
    categoryText?: TypographyPresentationProps;
    categoryImageWrapper?: InjectableCss;
    lineSeparator?: InjectableCss;
}

export const shopByCategoryStyles: ShopByCategoryStyles = {
    wrapper: {
        css: css`
            background: ${color("primary")};
        `,
    },
    container: {
        css: css`
            padding: 64px 0;
            ${layoutContainer};
        `,
    },
    titleText: {
        extendedStyles: {
            wrapper: {
                css: css`
                    margin-bottom: 0;
                    ${xScreen("lte", "md")} {
                        padding-left: 10px;
                    }
                    ${xScreen("lte", "sm")} {
                        padding-left: 0;
                    }
                `,
            },
            title: {
                color: "white",
                css: css`
                    && {
                        margin-bottom: 0;
                    }
                `,
            },
        },
    },
    categoriesWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            flex-wrap: wrap;

            ${xScreen("lte", "md")} {
                padding: 0 16px;
                column-gap: 48px;
                justify-content: center;
            }

            ${xScreen("lte", 767)} {
                padding: 0 8px;
                column-gap: 40px;
            }
        `,
    },
    categoryItem: {
        css: css`
            display: flex;
            align-items: flex-start;
            justify-content: center;
            margin-top: 32px;
            width: 136px;
        `,
    },
    categoryImage: {
        css: css`
            background: white;
            height: 125px;
            border-radius: 125px;
            border: 3px solid ${color("common.ui21")};
            overflow: hidden !important;
            box-sizing: border-box;
            padding: 5px;
            margin: auto;
            width: 125px;
            isolation: isolate;
        `,
    },
    categoryLink: {
        css: css`
            &,
            > span {
                width: 100%;
                display: flex;
                flex-direction: column;
                justify-content: center;
            }

            &:hover {
                text-decoration-color: ${color("text")};
            }
        `,
    },
    categoryText: {
        color: "white",
        size: 14,
        lineHeight: "18px",
        weight: 800,
        css: css`
            margin-top: 12px;
            margin-bottom: 0;
            text-align: center;
            width: 100%;
        `,
    },
    lineSeparator: {
        css: css`
            flex-basis: 100%;

            ${xScreen("gt", "lg")} {
                display: none;
            }
        `,
    },
};

const styles = shopByCategoryStyles;

const PopularCategoriesWidget: React.FC<Props> = ({
    fields,
    categoriesDataView,
    signInUrl,
    notFoundImagePath,
    categoryListPageUrl,
    loadCategories,
}) => {
    const [categories, setCategories] = React.useState<Category[]>([]);
    const [noCategoriesFound, setNoCategoriesFound] = React.useState(false);

    const filterTheCategories = () => {
        const categoriesId = fields?.categories.map(function (cat) {
            return cat.fields.link?.value || cat.fields.id;
        });

        const tempCategories: Category[] = [];

        setNoCategoriesFound(false);
        if (categoriesDataView?.value?.length && fields?.categories?.length) {
            categoriesDataView.value.forEach(category => {
                categoriesId.indexOf(category.id) > -1 && tempCategories.push(category);
            });
        }

        !tempCategories.length && setNoCategoriesFound(true);
        setCategories(tempCategories);
    };

    const getCategoryName = (category: Category) => {
        const fieldCategory = fields?.categories.filter(
            cat => (cat.fields.link?.value || cat.fields.id) === category.id,
        )[0];
        if (fieldCategory?.fields.title) {
            return fieldCategory.fields.title;
        }
        return category.shortDescription;
    };

    // to avoid memory leak, put the condition inside the useEffect to limit the number of rendering
    React.useEffect(() => {
        if (!categoriesDataView || !categoriesDataView.value) {
            loadCategories();
        }
    }, []);

    React.useEffect(() => {
        filterTheCategories();
    }, [categoriesDataView, fields.categories]);

    if (categoriesDataView?.isLoading || (!categories.length && !noCategoriesFound)) {
        return <></>;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.container}>
                <TitleTypography {...styles.titleText} data-test-selector="popularCategoriesTitle">
                    {translate("Popular Product Categories")}
                </TitleTypography>
                {categories.length > 0 && (
                    <StyledWrapper {...styles.categoriesWrapper}>
                        {categories.map((category, index) => (
                            <React.Fragment key={category.id}>
                                <StyledWrapper key={uniqueId()} {...styles.categoryItem}>
                                    <Link {...styles.categoryLink} href={category.path}>
                                        <LazyImage
                                            altText={category.name}
                                            {...styles.categoryImage}
                                            src={category.smallImagePath || notFoundImagePath}
                                        />
                                        <Typography {...styles.categoryText}>{getCategoryName(category)}</Typography>
                                    </Link>
                                </StyledWrapper>
                            </React.Fragment>
                        ))}
                        <StyledWrapper key={uniqueId()} {...styles.categoryItem}>
                            <Link {...styles.categoryLink} href={categoryListPageUrl}>
                                <Icon
                                    src={LogoShort}
                                    css={css`
                                        height: 125px;
                                        width: 125px;
                                        border-radius: 100%;
                                        border: 3px solid ${color("common.ui21")};
                                        box-shadow: inset 0 1px 3px 0 ${color("common.ui10", 0.5)};
                                        background: white;
                                        display: flex;
                                        justify-content: center;
                                        align-items: center;
                                    `}
                                    size={80}
                                />
                                <Typography {...styles.categoryText}>{translate("All Categories")}</Typography>
                            </Link>
                        </StyledWrapper>
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(PopularCategoriesWidget),
    definition: {
        group: "Xngage Custom" as any,
        fieldDefinitions: [
            {
                name: fields.categories,
                editorTemplate: "ListField",
                displayName: "Add Categories",
                defaultValue: [],
                fieldType: "General",
                getDisplay: (item: HasFields, state) => {
                    const { type, value } = item.fields.link || {};
                    const titleOverride = item.fields.title;
                    if (titleOverride) {
                        return titleOverride;
                    }
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        let displayValue = "";
                        const categories = (state as any).pageEditor.categories;
                        if (!categories) {
                            displayValue = "Loading...";
                        } else {
                            const matchingCategories = categories.filter((o: { id: string }) => o.id === value);
                            if (matchingCategories.length === 0) {
                                displayValue = "Unknown";
                            } else {
                                displayValue = matchingCategories[0].shortDescription;
                            }
                        }

                        return displayValue;
                    }
                },
                fieldDefinitions: [
                    {
                        name: "link",
                        displayName: "Select Category",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        allowCategories: item => true,
                        allowUrls: item => false,
                        isRequired: true,
                    },
                    {
                        name: "title",
                        displayName: "Category Title Override",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        // keeping id for backward compatibility with existing authored data
                        name: "id",
                        displayName: "Category ID",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isVisible: item => !item.fields.link?.type,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
